import {
  botQueryKey,
  useMutationCreateBot,
  useMutationUpdateBot,
  useQueryGetOneBot,
} from "@apis:bot/bot";
import {
  useMutationInsertDeleteUserBots,
  useQueryGetAllUserBotsByBotId,
  userBotQueryKey,
} from "@apis:user_bot/user_bot";
import { findMaskedSecrets, useSchemaValidation } from "@components:SchemaForm";
import { SuperDialog } from "@components:SuperDialog";
import { TabPanel } from "@components:TabPanel";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useRBACCheck } from "@hooks:useRBAC";
import { apiErrorDetail } from "@libs:axios";
import { Box, Tab, Tabs } from "@mui/material";
import { AISettings } from "@pages:settings/Tenent/Bots/BotSpecfications/AISettings/AISettings";
import { aiSettingsSchema } from "@pages:settings/Tenent/Bots/BotSpecfications/AISettings/schema";
import { General } from "@pages:settings/Tenent/Bots/BotSpecfications/General";
import { FormsInputProps } from "@pages:settings/Tenent/Bots/forms";
import { useToasterStoreActions } from "@stores:global";
import { SyntheticEvent, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FeatureFlag } from "@:flag";
import { RBACAction, RBACResource } from "@:rbac";

interface BotModalProps {
  isOpen: boolean;
  onClose: () => void;
  botId?: string;
}

export const BotModal = ({ isOpen, onClose, botId }: BotModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const { data, isLoading } = useQueryGetOneBot(
    { id: botId! },
    {
      enabled: !!botId,
    },
  );

  const canCreateUserBot = useRBACCheck(
    RBACAction.create,
    RBACResource.user_bots,
  );

  const canUpdateUserBot = useRBACCheck(
    RBACAction.create,
    RBACResource.user_bots,
  );

  const { data: userBots, isLoading: userBotsLoading } =
    useQueryGetAllUserBotsByBotId(
      {
        botId: botId!,
      },
      {
        enabled: !!botId,
      },
    );

  const [tab, setTab] = useState(0);

  const handleChangeTab = (_event: SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const methods = useForm<FormsInputProps>({
    defaultValues: {
      name: "",
      ai_settings: {},
      organization_id: "",
      enabled: true,
      user_ids: [],
    },
    values: {
      name: data?.name,
      ai_settings: data?.ai_settings,
      organization_id: data?.organization_id,
      enabled: data?.enabled,
      user_ids: userBots?.map((userBot) => userBot.userId) || [],
    },
    // we need to use mode: 'all' for MUI https://github.com/orgs/react-hook-form/discussions/2357#discussioncomment-42073
    mode: "all",
  });
  const {
    register,
    handleSubmit,
    formState,
    control,
    watch,
    unregister,
    reset,
    setValue,
    trigger,
    getValues,
  } = methods;

  // `ai_settings` is validated against its JSON Schema; the fields are not registered one by one.
  // A masked secret can be saved only where the API puts the stored secret back.
  const aiSettingsValidation = useSchemaValidation(
    aiSettingsSchema,
    "ai_settings",
    { watch, getValues },
    {
      extra: (value) =>
        findMaskedSecrets(aiSettingsSchema, value, data?.ai_settings)
          .filter((secret) => !secret.kept)
          .map(({ path }) => ({
            path,
            message: t("tenant.aiSettings.maskedSecret"),
          })),
    },
  );

  const mutationCreateDeleteBotUsers = useMutationInsertDeleteUserBots();

  // The API's reason (e.g. which settings field is invalid) follows the generic message
  const failureMessage = (message: string, error: unknown) => {
    const detail = apiErrorDetail(error);

    return (
      <span style={{ whiteSpace: "pre-line" }}>
        {detail ? `${message}\n${detail}` : message}
      </span>
    );
  };

  const mutationCreateBot = useMutationCreateBot({
    onError: (error) => {
      toastActions.setToaster({
        open: true,
        message: failureMessage(t("tenant.bots.createFailed"), error),
        variant: "error",
      });
    },
  });

  const mutationUpdateBot = useMutationUpdateBot({
    onError: (error) => {
      toastActions.setToaster({
        open: true,
        message: failureMessage(t("tenant.bots.updateFailed"), error),
        variant: "error",
      });
    },
  });

  return (
    <SuperDialog
      open={isOpen}
      onClose={onClose}
      title={botId ? t("tenant.bots.edit") : t("tenant.bots.add")}
      isLoading={isLoading || userBotsLoading}
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "90vh",
          maxHeight: "90vh",
          width: "50vw",
          minWidth: "950px",
          maxWidth: "1500px",
          overflowY: "hidden",
        },
      }}
      slotProps={{
        dialogContent: {
          sx: {
            overflowY: "hidden",
          },
        },
      }}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
          },
          label: t("common.save"),
          onClick: async () => {
            await handleSubmit(async (data) => {
              // The last change can still wait for its debounced validation
              if (!aiSettingsValidation.validateNow()) return;

              if (botId) {
                await mutationUpdateBot.mutateAsync({
                  id: botId,
                  ai_settings: data.ai_settings!,
                  name: data.name!,
                  ...(FeatureFlag.UpdateEnabled
                    ? { enabled: data.enabled! }
                    : {}),
                });

                if (Array.isArray(data.user_ids) && canUpdateUserBot) {
                  await mutationCreateDeleteBotUsers.mutateAsync({
                    botId: botId,
                    userIds: data.user_ids,
                  });
                }

                toastActions.setToaster({
                  open: true,
                  message: t("tenant.bots.updated"),
                  variant: "success",
                });
              } else {
                const createdBot = await mutationCreateBot.mutateAsync({
                  name: data.name!,
                  ai_settings: data.ai_settings!,
                  organization_id: data.organization_id!,
                  ...(FeatureFlag.UpdateEnabled
                    ? { enabled: data.enabled! }
                    : {}),
                });

                if (Array.isArray(data.user_ids) && canCreateUserBot) {
                  await mutationCreateDeleteBotUsers.mutateAsync({
                    botId: createdBot.id,
                    userIds: data.user_ids,
                  });
                }

                toastActions.setToaster({
                  open: true,
                  message: t("tenant.bots.created"),
                  variant: "success",
                });
              }

              await invalidateQueries.run([
                {
                  queryKey: botQueryKey._def,
                },
              ]);

              await invalidateQueries.run([
                {
                  queryKey: userBotQueryKey._def,
                },
              ]);

              onClose();
            })();
          },
          disabled: !formState.isValid || !aiSettingsValidation.isValid,
        },
      }}
    >
      <FormProvider {...methods}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tabs value={tab} onChange={handleChangeTab}>
            <Tab label={t("tenant.bots.tabs.general")} />
            <Tab label={t("tenant.bots.tabs.aiSettings")} />
          </Tabs>
        </Box>
        <TabPanel index={0} value={tab}>
          <General
            register={register}
            control={control}
            formState={formState}
            watch={watch}
            unregister={unregister}
            type={botId ? "update" : "create"}
            reset={reset}
            setValue={setValue}
            botId={botId}
            trigger={trigger}
          />
        </TabPanel>
        <TabPanel
          index={1}
          value={tab}
          sx={{
            overflowY: "auto",
            maxHeight: "calc(90vh - 200px)",
            height: "100%",
          }}
        >
          <AISettings
            botId={botId}
            stored={data?.ai_settings}
            errors={aiSettingsValidation.errors}
          />
        </TabPanel>
      </FormProvider>
    </SuperDialog>
  );
};
